import { projectTimezone } from '@dashboard/utils/projectTimezone'
import { defineStore } from 'pinia'
import api from '@dashboard/services/axiosApi'
import { isAxiosError } from 'axios'
import { useNotificationStore } from '@dashboard/stores/notificationStore'
import { klona } from 'klona'
import type {
  RightyThread,
  RightyMessage,
  RightyAction,
  RightyInvocationContext,
  RightyNestedContext,
  CreateThreadPayload,
  RecentCreation,
  RightyPrompt,
  StreamSegment,
  DurableTurnAccepted,
  DurableTurnSnapshot,
  DurableTurnPayload,
  PendingTurnObservation,
  DurableTurnError,
  DurableTurnErrorKind,
  RightyTurnEvent,
  RightyTurnProgress,
  RightyMessageOptions,
  RightyAttachment,
  RightyPlanWire,
  BuildPhaseMessage,
  RightyReportRange
} from '../types'
import type { ChangelogItem } from '../types'
import { getDrawerSuggestions, getExternalChipPrompts } from '../services/promptUtilities'
import { markTurnSubmitted, reportTurnTiming } from '../services/turnTiming'
import { observeTurnStream } from '../services/turnStream'
import { RightyAttachmentSchema } from '../services/attachments'
import type { TurnStreamObserver } from '../services/turnStream'
import { useConfigStore } from '@dashboard/stores/configStore'
import { z } from 'zod'
import {
  applyPhaseMessageEvent,
  BuildPhaseMessageSchema,
  BuildPhaseSchema,
  BuildPhaseStateSchema,
  BuildProgressSchema,
  BuildStageSchema,
  BuildStageEventSchema
} from '../services/phaseMessages'
import { campaignEditorContextSchema } from '@dashboard/modules/personalization/services/editor/rightyContext'
import { useReportStore } from '@dashboard/modules/dashboard/stores/reportStore'
import { useFlowMetricsStore } from '@dashboard/modules/flow/stores/flowMetricsStore'
import { buildContextFromRoute } from '../services/rightyContextBuilder'
import { pageEditorContextSchema, type PageEditorContext } from '@dashboard/modules/pages/services/pageRightyContext'
import type {
  CampaignEditorContext,
  MatrixSelectionRequest
} from '@dashboard/modules/personalization/services/editor/rightyContext'
const buildStageLabels = {
  designing: 'Designing your draft',
  preparing: 'Checking a few details',
  shaping: 'Shaping the build',
  revising: 'Thinking through the design…',
  reviewing: 'Reviewing the design',
  materializing: 'Preparing the next step',
  executing: 'Building your draft',
  verifying: 'Checking your draft',
  summarizing: 'Summarizing the changes',
  completed: 'Build complete',
  stopped: 'Build stopped',
  failed: 'Build incomplete'
}

import type { Config } from '@dashboard/types/config'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { computed, nextTick, onScopeDispose, readonly, ref, shallowRef, toRaw, watch } from 'vue'
import { useRoute } from 'vue-router'
type WithResolversPromiseConstructor = PromiseConstructor & {
  withResolvers<T>(): {
    promise: Promise<T>
    resolve: (value: T | PromiseLike<T>) => void
    reject: (reason?: unknown) => void
  }
}

function normalizePhpObject(value: unknown): unknown {
  return Array.isArray(value) && value.length === 0 ? {} : value
}

const PhpRecordSchema = z.preprocess(normalizePhpObject, z.record(z.unknown()))

// Full list reads (list_dimensions detail "full") return a JSON list; keep it as `{ items }`
// so a turn that ran one still parses instead of failing to load.
const ReceiptResultSchema = z.preprocess(
  (value) => (Array.isArray(value) && value.length > 0 ? { items: value } : value),
  PhpRecordSchema
)

const RightyInvocationContextSchema: z.ZodType<RightyInvocationContext, z.ZodTypeDef, unknown> = z.object({
  module: z.enum([
    'dashboard',
    'flow',
    'widget',
    'segment',
    'campaign',
    'offer',
    'integration',
    'insights',
    'settings',
    'visitors',
    'email',
    'page'
  ]),
  route: z.string(),
  routeName: z.string().optional(),
  resourceType: z
    .enum(['flow', 'widget', 'campaign', 'dimension', 'question', 'offer', 'visitor', 'email', 'page'])
    .optional(),
  resourceId: z.string().optional(),
  campaignEditor: campaignEditorContextSchema.optional(),
  pageEditor: pageEditorContextSchema.optional(),
  resourceName: z.string().optional(),
  additionalContext: z
    .preprocess(
      normalizePhpObject,
      z.object({
        editingNodeId: z.string().optional(),
        widgetType: z.string().optional(),
        isQuestion: z.boolean().optional(),
        visitorData: z
          .object({
            email: z.string(),
            segments: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
                dimensionName: z.string()
              })
            ),
            integrationData: z
              .object({
                tags: z.array(z.string()),
                customFields: z.preprocess(normalizePhpObject, z.record(z.string()))
              })
              .optional(),
            recentActivity: z
              .object({
                pagesVisited: z.number(),
                lastSeen: z.string(),
                firstSeen: z.string()
              })
              .optional()
          })
          .optional()
      })
    )
    .optional(),
  nestedContext: z
    .object({
      editingType: z.enum([
        'offer',
        'form',
        'question',
        'widget',
        'condition',
        'redirect',
        'message',
        'data',
        'code',
        'embed'
      ]),
      nodeId: z.string().optional(),
      resourceId: z.string().optional(),
      resourceName: z.string().optional(),
      isForm: z.boolean().optional()
    })
    .optional(),
  forceAutoApply: z.boolean().optional(),
  reportRange: z.object({ start: z.string(), end: z.string(), timezone: z.string() }).optional()
})

const TurnStateSchema = z.enum([
  'submitted',
  'running',
  'succeeded',
  'failed',
  'waiting',
  'stopped',
  'awaiting_approval'
])
const ApprovalSchema = z.object({
  state: z.enum(['pending', 'confirming', 'approved', 'cancelled']),
  title: z.string(),
  body: z.string(),
  confirmLabel: z.string(),
  resourceKind: z.string(),
  resourceName: z.string().nullable(),
  cancelReason: z.enum(['user', 'stopped']).nullable()
})
const PlanSchema = z.object({
  playbook: z.enum(['subscriberSurvey', 'leadGenQuiz', 'smartCtaFunnel']).nullable().default(null),
  stage: BuildStageSchema.optional(),
  phaseMessages: z
    .array(
      z.object({
        id: z.number(),
        key: z.string(),
        phase: BuildPhaseSchema,
        revision: z.number(),
        state: BuildPhaseStateSchema
      })
    )
    .optional(),
  phase: z.enum(['planning', 'executing', 'summarizing', 'completed', 'stopped', 'failed']),
  title: z.string().nullable(),
  summary: z.string().nullable(),
  todos: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      kind: z.string(),
      state: z.enum(['pending', 'active', 'done', 'partial', 'failed', 'not_built'])
    })
  )
})
const TurnStepSchema = z.object({ step: z.number().int().nonnegative(), text: z.string() })
const ReceiptSchema = z.object({
  id: z.number(),
  providerCallId: z.string(),
  tool: z.string(),
  step: z.number().int().nonnegative().optional(),
  // A planned receipt that has not started yet has no first event, so the server sends null.
  firstSequence: z
    .number()
    .int()
    .nonnegative()
    .nullish()
    .transform((sequence) => sequence ?? undefined),
  kind: z.enum(['mutation', 'read', 'interaction']).optional(),
  label: z
    .string()
    .nullish()
    .transform((label) => label ?? undefined),
  state: z.enum([
    'planned',
    'dispatching',
    'running',
    'succeeded',
    'failed',
    'waiting_for_user',
    'handed_off',
    'awaiting_approval',
    'cancelled'
  ]),
  approval: ApprovalSchema.nullable().optional(),
  result: ReceiptResultSchema.nullable(),
  readback: PhpRecordSchema.nullable().optional(),
  error: z
    .preprocess(normalizePhpObject, z.object({ code: z.string().optional(), message: z.string().optional() }))
    .nullable()
})
const TurnSnapshotSchema: z.ZodType<DurableTurnSnapshot, z.ZodTypeDef, unknown> = z.object({
  id: z.string(),
  state: TurnStateSchema,
  steps: z.array(TurnStepSchema).optional(),
  plan: PlanSchema.nullable().optional(),
  phaseMessages: z.array(BuildPhaseMessageSchema).optional(),
  buildProgress: BuildProgressSchema.nullable().optional(),
  taskId: z.string(),
  revision: z.number(),
  assistantMessageId: z.number().nullable(),
  failure: z.string().nullable(),
  taskState: z.string().nullable().optional(),
  lastEventSequence: z.number(),
  receipts: z.array(ReceiptSchema),
  deliverables: z.array(z.object({ key: z.string(), state: z.string() })),
  assistant: z
    .object({ id: z.number(), content: z.string().nullable(), metadata: PhpRecordSchema.optional() })
    .nullable()
})

const FAILED_TURN_MESSAGE = 'Righty could not complete that request.'
const TurnPlanOptionSchema = z.union([
  z.object({
    playbook: z.enum(['subscriberSurvey', 'leadGenQuiz', 'smartCtaFunnel']),
    flowId: z.string().startsWith('ofn_')
  }),
  z.object({
    pageBuild: z.number().int().positive(),
    resume: z.literal(true).optional(),
    chain: z.literal(true).optional()
  })
])
/** A thread's newest turn when it failed, so the dock offers Retry after a reload too. */
const ThreadRetrySchema = z.object({
  text: z.string(),
  plan: TurnPlanOptionSchema.nullable(),
  label: z.string().nullable()
})
const PendingTurnObservationSchema = z
  .object({
    clientTurnId: z.string().uuid(),
    payload: z.object({
      threadId: z.number().int(),
      clientTurnId: z.string().uuid(),
      text: z.string(),
      plan: TurnPlanOptionSchema.optional(),
      label: z.string().optional(),
      attachmentIds: z.array(z.number().int()).optional(),
      pageContext: z.object({
        currentPage: z.string(),
        pageTitle: z.string().nullable().default(null),
        context: RightyInvocationContextSchema.nullable(),
        tabId: z.string().nullable()
      }),
      task: z.union([
        z.object({ mode: z.literal('continue'), taskId: z.string() }),
        z.object({ mode: z.literal('start_new'), goal: z.string() })
      ]),
      parentTurnId: z.string().nullable()
    }),
    turnId: z.string().nullable(),
    optimisticMessageId: z.number(),
    cursor: z.number().int().nonnegative(),
    state: TurnStateSchema.optional(),
    receipts: z.array(ReceiptSchema).optional(),
    plan: PlanSchema.nullable().optional(),
    phaseMessages: z.array(BuildPhaseMessageSchema).optional(),
    buildStage: BuildStageEventSchema.extend({ startedAt: z.number() }).optional(),
    buildProgress: BuildProgressSchema.optional(),
    attachments: z.array(RightyAttachmentSchema).optional(),
    progress: z
      .object({
        started: z.boolean(),
        thinking: z.boolean().optional(),
        step: z.number().int().nonnegative(),
        content: z.string(),
        interim: z.array(z.union([TurnStepSchema, z.string().transform((text) => ({ step: 0, text }))])),
        tools: z.array(
          z.object({
            name: z.string(),
            step: z.number().int().nonnegative().optional(),
            providerCallId: z.string().optional(),
            firstSequence: z.number().int().nonnegative().optional(),
            status: z.enum(['pending', 'running', 'completed', 'error', 'stopped', 'cancelled']),
            description: z.string().optional(),
            arguments: PhpRecordSchema.optional(),
            error: z.string().optional()
          })
        )
      })
      .optional()
  })
  .refine((observation) => observation.clientTurnId === observation.payload.clientTurnId)

export interface ToolCall {
  name: string
  step?: number
  providerCallId?: string
  firstSequence?: number
  status: RightyTurnProgress['tools'][number]['status']
  description?: string
  arguments?: Record<string, any>
  error?: string
}

/** An observed turn on a hosted page once it settled, with its terminal metadata, for that page's editor to follow. */
export interface SettledPageTurn {
  pageId: string
  turnId: string
  metadata: Record<string, unknown>
}

export const useRightyStore = defineStore('righty', () => {
  const route = useRoute()

  let sidebarState: { open: boolean; threadId: number | null } = { open: false, threadId: null }
  try {
    const saved = JSON.parse(sessionStorage.getItem('righty:sidebar') || 'null')
    if (saved && typeof saved.open === 'boolean' && (saved.threadId === null || Number.isInteger(saved.threadId))) {
      sidebarState = saved
    }
  } catch {
    // Storage can be unavailable in private browsing.
  }
  let restoredThreadId = sidebarState.threadId

  // State
  const isDrawerOpen = ref(sidebarState.open)
  const focusInputOnOpen = ref(false)
  const threads = ref<RightyThread[]>([])
  const currentThread = ref<RightyThread | null>(null)
  const messages = ref<RightyMessage[]>([])
  const isLoading = ref(false)
  const isStreaming = ref(false)
  const undoingMessageId = ref<number | null>(null)
  const streamingContent = ref('')
  const streamingStartTime = ref<number | null>(null) // Timestamp when streaming started
  const isResearching = ref(false) // True when working on tool calls
  const pendingAction = ref<RightyAction | null>(null)
  const error = ref<string | null>(null)
  const currentToolCalls = ref<ToolCall[]>([])
  const streamingSegments = ref<StreamSegment[]>([]) // Interleaved text + tool segments for inline display
  const pendingQuestion = ref<StreamSegment | null>(null) // Active question awaiting user response
  const isReadyToChat = ref(false) // Shows suggestions UI without creating a thread yet
  const invocationContext = ref<RightyInvocationContext | null>(null)
  let campaignEditorProvider: ((selection?: MatrixSelectionRequest) => Promise<CampaignEditorContext | null>) | null =
    null
  let matrixSelectionRequest: MatrixSelectionRequest | undefined
  let matrixSelectionContext: CampaignEditorContext | null = null
  let capturingEditorContext = false
  function registerCampaignEditorContext(
    provider: (selection?: MatrixSelectionRequest) => Promise<CampaignEditorContext | null>
  ) {
    campaignEditorProvider = provider
    return () => {
      if (campaignEditorProvider === provider) {
        campaignEditorProvider = null
      }
    }
  }
  let pageEditorProvider: (() => Promise<PageEditorContext | null>) | null = null
  /** The page editor captures its selection and rendered revision when a turn is sent, never earlier. */
  function registerPageEditorContext(provider: () => Promise<PageEditorContext | null>) {
    pageEditorProvider = provider
    return () => {
      if (pageEditorProvider === provider) {
        pageEditorProvider = null
      }
    }
  }

  // Dynamic prompts registered by components (e.g., conditional prompts on VisitorDetail)
  const dynamicPrompts = ref<RightyPrompt[]>([])

  // Empty assistant state (generic: blank emails today)
  const syntheticWelcomeMessage = ref<RightyMessage | null>(null)
  const isEmptyAssistantActive = ref(false)
  const emptyAssistantContext = ref<{ type: string; id: string; name?: string } | null>(null)

  // Type-specific computed aliases
  const activeTaskId = ref<string | null>(null)
  const waitingTurnId = ref<string | null>(null)
  const observationGeneration = ref(0)
  const pendingObservation = ref<PendingTurnObservation | null>(null)
  const durableError = ref<DurableTurnError | null>(null)
  const lastFailedPayload = ref<Pick<DurableTurnPayload, 'text' | 'plan' | 'label'> | null>(null)
  const lastFailedAttachments = ref<RightyAttachment[]>([])
  const hasTurnStarted = ref(false)
  const interimText = ref<string[]>([])
  const stopRequested = ref(false)
  let streamObserver: TurnStreamObserver | null = null
  let settlingTurn = false

  function stopObserving() {
    streamObserver?.stop()
    streamObserver = null
  }
  onScopeDispose(stopObserving)
  const isEmptyEmailAssistantActive = computed(
    () => isEmptyAssistantActive.value && emptyAssistantContext.value?.type === 'email'
  )

  // Undo/changelog state for plan execution
  const configSnapshotBeforeBuild = ref<Config | null>(null)
  const lastBuildChangelog = ref<ChangelogItem[]>([])
  const canUndo = computed(() => configSnapshotBeforeBuild.value !== null)

  // Per-message built state and changelog tracking
  // This persists changelogs with their source messages so they stay in the thread
  const builtMessageChangelogs = ref<Map<number, ChangelogItem[]>>(new Map())

  // Durable workers carry the originating editor tab id with every config broadcast.
  const resolveEditorTabId = (): string | null => useConfigStore().tabId?.() ?? null

  // Computed
  const teamPid = computed(() => route.params.teamPid as string)
  const hasThreads = computed(() => threads.value.length > 0)
  const currentPage = computed(() => route.path)
  const inputMode = computed(() => {
    if (isStreaming.value) {
      return stopRequested.value ? 'stopping' : 'stop'
    }
    return pendingObservation.value || pendingQuestion.value ? 'blocked' : 'send'
  })

  /**
   * Contextual suggestions for the drawer's "Try Asking" chips.
   * Always returns exactly 4 prompts, merging dynamic + route + defaults.
   */
  const contextualSuggestions = computed<RightyPrompt[]>(() => getDrawerSuggestions(dynamicPrompts.value, route))

  /**
   * Contextual prompts for external chips (above the trigger button).
   * Returns up to 3 prompts, only if there are actual contextual prompts.
   */
  const externalChipPrompts = computed<RightyPrompt[]>(() => getExternalChipPrompts(dynamicPrompts.value, route))

  // Actions
  function openDrawer() {
    isDrawerOpen.value = true
    // Automatically show the ready-to-chat state when opening
    if (!currentThread.value) {
      isReadyToChat.value = true
    }
    void recordAnalyticsEvent(AnalyticsEventTypes.RIGHTY_OPENED)
    void loadThreads()
  }

  function closeDrawer() {
    isDrawerOpen.value = false
    focusInputOnOpen.value = false
  }

  function toggleDrawer() {
    if (isDrawerOpen.value) {
      closeDrawer()
    } else {
      openDrawer()
    }
  }

  async function loadThreads() {
    if (!teamPid.value || teamPid.value === 'demo') {
      return
    }
    const generation = observationGeneration.value

    try {
      isLoading.value = true
      const { data } = await api.get(`/api/${teamPid.value}/righty/threads`)
      threads.value = data.threads
      const pendingThread = threads.value.find(
        (thread) => sessionStorage.getItem(observationStorageKey(thread.id)) !== null
      )
      if (generation === observationGeneration.value && !currentThread.value) {
        const restoreId = restoredThreadId
        const threadToRestore = threads.value.find((thread) => thread.id === restoreId)
        restoredThreadId = null
        if (threadToRestore || pendingThread) {
          await selectThread((threadToRestore || pendingThread)!.id)
        } else if (restoreId !== null) {
          // A quick edit that was open is never listed; reopen it, and forget an ID that no longer opens.
          await selectThread(restoreId, { quiet: true })
        }
      }
    } catch (err: unknown) {
      if (generation !== observationGeneration.value) {
        return
      }
      setDurableError('general', errorMessage(err, 'Failed to load threads'))
      console.error('Failed to load threads:', err)
    } finally {
      if (generation === observationGeneration.value) {
        isLoading.value = false
      }
    }
  }

  async function createThread(payload?: CreateThreadPayload): Promise<RightyThread | null> {
    if (!teamPid.value || teamPid.value === 'demo') {
      return null
    }

    try {
      isLoading.value = true
      const { data } = await api.post(`/api/${teamPid.value}/righty/threads`, payload || {})
      const newThread = data.thread as RightyThread
      // A quick edit stays out of the history list; the dock still shows it as the current conversation.
      if (payload?.source !== 'quick_edit') {
        threads.value.unshift(newThread)
      }
      return newThread
    } catch (err: any) {
      error.value = err.message || 'Failed to create thread'
      console.error('Failed to create thread:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function selectThread(threadId: number, options: { quiet?: boolean } = {}): Promise<boolean> {
    if (!teamPid.value || teamPid.value === 'demo') {
      return false
    }
    let selectionGeneration = ++observationGeneration.value
    restoredThreadId = null
    stopObserving()

    try {
      isLoading.value = true
      const { data } = await api.get(`/api/${teamPid.value}/righty/threads/${threadId}`)
      if (selectionGeneration !== observationGeneration.value) {
        return false
      }
      resetConversationState(false)
      selectionGeneration = observationGeneration.value
      currentThread.value = data.thread
      const savedEditor = campaignEditorContextSchema.safeParse(currentThread.value?.context?.campaignEditor)
      if (savedEditor.success && savedEditor.data.matrix?.selection) {
        delete savedEditor.data.matrix.selection.opening
        matrixSelectionContext = savedEditor.data
      }
      messages.value = data.messages
      isReadyToChat.value = false
      activeTaskId.value = typeof data.activeTask?.id === 'string' ? data.activeTask.id : null
      waitingTurnId.value = typeof data.waitingTurnId === 'string' ? data.waitingTurnId : null
      pendingQuestion.value = null
      if (waitingTurnId.value) {
        const lastAssistant = [...messages.value].reverse().find((message) => message.role === 'assistant')
        pendingQuestion.value = questionFromMetadata(lastAssistant?.metadata, lastAssistant?.content)
      }
      const retry = ThreadRetrySchema.safeParse(data.retry)
      if (retry.success) {
        const failedMessage = [...messages.value]
          .reverse()
          .find((message) => message.role === 'user' && message.content === retry.data.text)
        lastFailedPayload.value = {
          text: retry.data.text,
          ...(retry.data.plan ? { plan: retry.data.plan } : {}),
          ...(retry.data.label ? { label: retry.data.label } : {})
        }
        lastFailedAttachments.value = failedMessage?.metadata?.attachments ?? []
        setDurableError('terminal', FAILED_TURN_MESSAGE)
      }

      const outstandingTurnId =
        typeof data.outstandingTurnId === 'string'
          ? data.outstandingTurnId
          : typeof data.outstandingTurn?.turnId === 'string'
            ? data.outstandingTurn.turnId
            : null
      const persisted = loadPendingObservation(threadId)
      if (persisted && (persisted.turnId !== null || outstandingTurnId === null)) {
        const persistedUserMessage = [...messages.value]
          .reverse()
          .find((message) => message.role === 'user' && message.content === persisted.payload.text)
        if (persisted.turnId !== null && persistedUserMessage) {
          persisted.optimisticMessageId = persistedUserMessage.id
        } else if (
          persisted.clientTurnId === data.outstandingTurn?.clientTurnId &&
          typeof data.outstandingTurn?.userMessageId === 'number'
        ) {
          persisted.optimisticMessageId = data.outstandingTurn.userMessageId
        }
        pendingObservation.value = persisted
      } else if (
        outstandingTurnId &&
        typeof data.outstandingTurn?.clientTurnId === 'string' &&
        typeof data.outstandingTurn?.taskId === 'string'
      ) {
        const lastUserMessage = [...messages.value].reverse().find((message) => message.role === 'user')
        if (lastUserMessage) {
          const resumed: PendingTurnObservation = {
            clientTurnId: data.outstandingTurn.clientTurnId,
            payload: {
              threadId,
              clientTurnId: data.outstandingTurn.clientTurnId,
              text: lastUserMessage.content,
              pageContext: {
                currentPage: currentPage.value,
                pageTitle: lastUserMessage.pageContext?.title ?? null,
                context: invocationContext.value,
                tabId: resolveEditorTabId()
              },
              task: { mode: 'continue', taskId: data.outstandingTurn.taskId },
              parentTurnId:
                typeof data.outstandingTurn.parentTurnId === 'string' ? data.outstandingTurn.parentTurnId : null
            },
            turnId: outstandingTurnId,
            optimisticMessageId: lastUserMessage.id,
            cursor: 0
          }
          persistPendingObservation(resumed)
        }
      }
      if (pendingObservation.value?.turnId === null) {
        restoreOptimisticUserMessage(pendingObservation.value)
      }
      if (pendingObservation.value) {
        void retryObservation()
      } else if (outstandingTurnId) {
        setDurableError(
          'persistence',
          'Righty is still working on this request, but its local observation state is unavailable. Reopen this conversation to retry observation.'
        )
      }
      return true
    } catch (err: unknown) {
      if (selectionGeneration !== observationGeneration.value || options.quiet) {
        return false
      }
      error.value = 'Failed to load conversation.'
      setDurableError('snapshot', error.value)
      console.error('Failed to load thread:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function deleteThread(threadId: number) {
    if (!teamPid.value || teamPid.value === 'demo') {
      return
    }

    try {
      await api.delete(`/api/${teamPid.value}/righty/threads/${threadId}`)
      sessionStorage.removeItem(observationStorageKey(threadId))
      threads.value = threads.value.filter((t) => t.id !== threadId)
      if (currentThread.value?.id === threadId) {
        resetConversationState(false)
      }
    } catch (err: unknown) {
      const serverMessage = isAxiosError<{ message?: unknown }>(err) ? err.response?.data?.message : undefined
      error.value = typeof serverMessage === 'string' ? serverMessage : errorMessage(err, 'Failed to delete thread')
      useNotificationStore().addError(error.value)
      setDurableError('persistence', error.value)
      console.error('Failed to delete thread:', err)
    }
  }

  /** The range and timezone `fetchFlowExecutionMetrics` requests, so Righty quotes the numbers on screen. */
  function currentReportRange(): RightyReportRange {
    const { dateRange } = useReportStore()
    return {
      start: dateRange.start.toISOString(),
      end: dateRange.end.toISOString(),
      timezone: useFlowMetricsStore().reportingTimezone ?? dateRange.timezone ?? projectTimezone()
    }
  }

  async function sendMessage(message: string, options: RightyMessageOptions = {}) {
    if (!teamPid.value || teamPid.value === 'demo' || pendingObservation.value || capturingEditorContext) {
      return
    }
    const reportRange = currentReportRange()
    let context = invocationContext.value ? klona(toRaw(invocationContext.value)) : null
    const provider = campaignEditorProvider
    if (provider || matrixSelectionContext) {
      capturingEditorContext = true
      try {
        const campaignEditor = matrixSelectionContext ?? (await provider?.(matrixSelectionRequest))
        if (provider !== campaignEditorProvider) {
          throw new Error('The editor changed. Send your message again from the current page.')
        }
        if (campaignEditor) {
          context = {
            ...(context ?? { module: 'campaign', route: currentPage.value }),
            resourceType: 'campaign',
            resourceId: campaignEditor.campaignId,
            campaignEditor: campaignEditorContextSchema.parse({
              ...campaignEditor,
              ...(campaignEditor.matrix
                ? {
                    matrix: {
                      ...campaignEditor.matrix,
                      fill: !campaignEditor.matrix.selection && options.fillMatrix === true
                    }
                  }
                : {})
            })
          }
        }
      } catch (failure) {
        setDurableError(
          'general',
          failure instanceof Error ? failure.message : 'The editor context could not be captured.'
        )
        return
      } finally {
        capturingEditorContext = false
      }
    }
    const pageProvider = pageEditorProvider
    if (pageProvider && !context?.campaignEditor) {
      capturingEditorContext = true
      try {
        const pageEditor = await pageProvider()
        if (pageProvider !== pageEditorProvider) {
          throw new Error('The editor changed. Send your message again from the current page.')
        }
        if (pageEditor) {
          context = {
            ...(context ?? { module: 'page', route: currentPage.value }),
            resourceType: 'page',
            resourceId: pageEditor.pageId,
            pageEditor: pageEditorContextSchema.parse(pageEditor)
          }
        }
      } catch (failure) {
        setDurableError(
          'general',
          failure instanceof Error ? failure.message : 'The editor context could not be captured.'
        )
        return
      } finally {
        capturingEditorContext = false
      }
    }
    if (matrixSelectionRequest && !context?.campaignEditor?.matrix?.selection) {
      setDurableError('general', 'Open the Matrix and select the cells again before asking Righty.')
      return
    }
    if (options.quickEdit || !currentThread.value) {
      if (options.quickEdit) {
        restoredThreadId = null
        resetConversationState(false)
      }
      const thread = await createThread(options.quickEdit ? { source: 'quick_edit' } : undefined)
      if (!thread) {
        setDurableError('general', 'Failed to start conversation')
        return
      }
      currentThread.value = thread
      isReadyToChat.value = false
    }
    context = { ...(context ?? buildContextFromRoute(route)), reportRange }

    const editorPage = context?.campaignEditor
    const editorQuery = editorPage ? new URLSearchParams({ url: editorPage.pageUrl }) : null
    const pageContext = {
      currentPage: editorQuery ? `${currentPage.value}?${editorQuery}` : currentPage.value,
      pageTitle:
        document.title
          .split(' / ')[0]
          ?.replace(/^RightMessage$/, '')
          .trim()
          .slice(0, 160) || null,
      context,
      tabId: resolveEditorTabId()
    }
    const attachments = options.attachments ? klona(toRaw(options.attachments)) : []
    const userMessage: RightyMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      pageContext: { path: pageContext.currentPage, title: pageContext.pageTitle },
      ...(attachments.length || options.label
        ? {
            metadata: {
              ...(attachments.length ? { attachments } : {}),
              ...(options.label ? { label: options.label } : {})
            }
          }
        : {}),
      createdAt: new Date().toISOString()
    }
    messages.value.push(userMessage)
    const clientTurnId = crypto.randomUUID()
    markTurnSubmitted(clientTurnId)
    const task =
      activeTaskId.value && !options.plan
        ? { mode: 'continue' as const, taskId: activeTaskId.value }
        : { mode: 'start_new' as const, goal: message }
    const payload: DurableTurnPayload = {
      threadId: currentThread.value.id,
      clientTurnId,
      text: message,
      ...(options.plan ? { plan: options.plan } : {}),
      ...(options.label ? { label: options.label } : {}),
      ...(attachments.length ? { attachmentIds: attachments.map((attachment) => attachment.id) } : {}),
      pageContext,
      task,
      parentTurnId: options.plan ? null : waitingTurnId.value
    }
    persistPendingObservation({
      clientTurnId,
      payload,
      turnId: null,
      optimisticMessageId: userMessage.id,
      cursor: 0,
      ...(attachments.length ? { attachments } : {})
    })
    if (context?.campaignEditor?.matrix?.selection) {
      matrixSelectionContext = klona(context.campaignEditor)
      if (matrixSelectionContext.matrix?.selection) {
        delete matrixSelectionContext.matrix.selection.opening
      }
      matrixSelectionRequest = undefined
    }
    observationGeneration.value++
    void recordAnalyticsEvent(AnalyticsEventTypes.RIGHTY_MESSAGE_SENT, {
      threadId: currentThread.value.id,
      messageLength: message.length,
      hasContext: !!invocationContext.value,
      attachmentCount: attachments.length
    })
    await retryObservation()
  }

  async function submitTurn(payload: DurableTurnPayload): Promise<DurableTurnAccepted> {
    if (!(await useConfigStore().saveCurrentDraft())) {
      throw new Error('Save your pending changes before asking Righty to continue.')
    }
    let lastError: unknown
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.post<DurableTurnAccepted>(`/api/${teamPid.value}/righty/turns`, payload)
        return data
      } catch (error) {
        if (isDefinitiveSubmitFailure(error)) {
          throw new DefinitiveSubmitError(submitFailureMessage(error))
        }
        lastError = error
        if (attempt === 2) {
          throw error
        }
        await sleep(250 * (attempt + 1))
      }
    }
    throw lastError
  }

  function applyAndSettleTurnSnapshot(turn: DurableTurnSnapshot, observation: PendingTurnObservation) {
    if (!applyTurnSnapshot(turn)) {
      return
    }
    if (['succeeded', 'failed', 'waiting', 'stopped'].includes(turn.state)) {
      if (turn.state === 'failed') {
        lastFailedPayload.value = observation.payload
        lastFailedAttachments.value = observation.attachments ?? []
      }
      clearPendingObservation(observation)
      stopObserving()
      isStreaming.value = false
      streamingStartTime.value = null
      isResearching.value = false
      stopRequested.value = false
      const pageId = observationPageId(observation)
      if (pageId && observation.turnId) {
        settledPageTurn.value = { pageId, turnId: observation.turnId, metadata: turn.assistant?.metadata ?? {} }
      }
      followChainedTurn(turn, observation)
    }
  }

  /**
   * A dashboard page build ends by having the server start the next turn of the same request in the same conversation
   * (`nextTurn`): the page's audiences and tests (`layers`), or its new quiz or survey, which the layer turn may chain in
   * turn. Nothing is submitted here: reloading the conversation shows the chained request and attaches to the running
   * turn through the outstanding-turn path a reload uses. That path keeps the page editor's context out of the
   * observation, so the editor stays usable while a flow builds; a layer turn's page comes from its chained request
   * instead (`observationPageId`), so the editor holds its layers as for any turn on the page. It waits a tick so
   * watchers see the previous turn settle (the config refetch) before the dock streams again.
   */
  function followChainedTurn(turn: DurableTurnSnapshot, observation: PendingTurnObservation) {
    const chained = z
      .object({ nextTurn: z.object({ id: z.string(), kind: z.string().optional(), pageId: z.string().optional() }) })
      .safeParse(turn.assistant?.metadata)
    if (turn.state !== 'succeeded' || !chained.success) {
      return
    }
    const next = chained.data.nextTurn
    const threadId = observation.payload.threadId
    const generation = observationGeneration.value
    void nextTick(async () => {
      if (
        generation !== observationGeneration.value ||
        currentThread.value?.id !== threadId ||
        pendingObservation.value
      ) {
        return
      }
      isStreaming.value = true
      streamingStartTime.value = Date.now()
      chainingPageId.value = next.kind === 'layers' ? (next.pageId ?? null) : null
      try {
        await selectThread(threadId)
      } finally {
        chainingPageId.value = null
      }
      if (!pendingObservation.value) {
        isStreaming.value = false
        streamingStartTime.value = null
      }
    })
  }

  async function loadTurnSnapshot(observation: PendingTurnObservation, generation: number) {
    try {
      const { data } = await api.get<unknown>(`/api/${teamPid.value}/righty/turns/${observation.turnId}`)
      const { turn } = z.object({ turn: TurnSnapshotSchema }).parse(data)
      if (!isActiveObservation(observation, generation)) {
        return
      }
      if (durableError.value?.kind === 'stream' || durableError.value?.kind === 'snapshot') {
        clearError()
      }
      restoreOptimisticUserMessage(observation)
      applyAndSettleTurnSnapshot(turn, observation)
    } catch (err) {
      if (isActiveObservation(observation, generation)) {
        setDurableError('snapshot', 'Turn accepted, but its latest state could not be loaded.')
      }
      throw err
    }
  }

  function renderStreamingMessage(observation: PendingTurnObservation) {
    const existing = messages.value.find(
      (message) =>
        message.role === 'assistant' &&
        !message.metadata?.buildPhase &&
        message.metadata?.rightyTurnId === observation.turnId
    )
    if (existing) {
      existing.content = streamingContent.value
    } else if (streamingContent.value) {
      messages.value.push({
        id: -observation.optimisticMessageId,
        role: 'assistant',
        content: streamingContent.value,
        createdAt: new Date().toISOString(),
        metadata: { rightyTurnId: observation.turnId ?? undefined, rightyProgress: observation.progress }
      })
    }
  }

  function applyTurnEvent(event: RightyTurnEvent, observation: PendingTurnObservation, generation: number) {
    if (!isActiveObservation(observation, generation)) {
      return
    }
    if (event.sequence <= observation.cursor) {
      return
    }
    if (event.sequence !== observation.cursor + 1) {
      void streamObserver?.replay()
      return
    }
    const progress = observation.progress
    if (!progress) {
      return
    }
    if (durableError.value?.kind === 'stream') {
      clearError()
    }
    const parsedPayload = PhpRecordSchema.safeParse(event.payload)
    if (!parsedPayload.success) {
      return
    }
    const payload = parsedPayload.data
    let rendered = false
    if (event.type.startsWith('phase_message_') && observation.turnId) {
      observation.phaseMessages ??= []
      if (!applyPhaseMessageEvent(observation.phaseMessages, event, observation.turnId)) {
        void reconcileTurn(observation, generation).catch(() => {})
        return
      }
      for (const message of observation.phaseMessages) {
        upsertPhaseMessage(message)
      }
      rendered = true
    } else if (event.type === 'build_stage') {
      const parsed = BuildStageEventSchema.safeParse(payload)
      if (parsed.success) {
        observation.buildStage = { ...parsed.data, startedAt: Date.now() }
        delete observation.buildProgress
        if (observation.plan) {
          observation.plan.stage = parsed.data.stage
        }
        rendered = true
      }
    } else if (event.type === 'build_progress') {
      const parsed = BuildProgressSchema.safeParse(payload)
      if (parsed.success) {
        observation.buildProgress = parsed.data
        rendered = true
      }
    } else if (event.type === 'started') {
      observation.state = 'running'
      progress.started = true
      hasTurnStarted.value = true
      rendered = true
    } else if (event.type === 'provider_step_started' && typeof payload.step === 'number') {
      if (progress.content && payload.step > progress.step) {
        progress.interim.push({ step: progress.step, text: progress.content })
      }
      progress.step = payload.step
      progress.content = ''
      progress.thinking = true
      currentToolCalls.value = currentToolCalls.value.filter(
        (call) =>
          call.step !== payload.step ||
          observation.receipts?.some((receipt) => receipt.providerCallId === call.providerCallId)
      )
      progress.tools = currentToolCalls.value
      streamingContent.value = ''
      interimText.value = progress.interim.map((note) => note.text)
      renderStreamingMessage(observation)
      rendered = true
    } else if (event.type === 'plan') {
      const parsed = PlanSchema.safeParse(payload.plan)
      if (parsed.success) {
        observation.plan = parsed.data
        recordPlaybookProgress(parsed.data, observation.turnId ?? observation.clientTurnId)
        rendered = parsed.data.todos.length > 0
      }
    } else if (event.type === 'text_delta' && typeof payload.text === 'string' && typeof payload.step === 'number') {
      progress.thinking = false
      if (payload.step > progress.step) {
        if (progress.content) {
          progress.interim.push({ step: progress.step, text: progress.content })
        }
        progress.step = payload.step
        progress.content = ''
      }
      if (payload.step === progress.step) {
        // A retried attempt of the same step starts over instead of appending to the dead attempt's text.
        progress.content = payload.restart === true ? payload.text : progress.content + payload.text
        streamingContent.value = progress.content
        interimText.value = progress.interim.map((note) => note.text)
        renderStreamingMessage(observation)
        rendered = payload.text.length > 0
      }
    } else if (event.type === 'tool_started' || event.type === 'tool_progress' || event.type === 'receipt') {
      progress.thinking = false
      if (typeof payload.step === 'number' && payload.step > progress.step) {
        if (progress.content) {
          progress.interim.push({ step: progress.step, text: progress.content })
        }
        progress.step = payload.step
        progress.content = ''
        streamingContent.value = ''
        interimText.value = progress.interim.map((note) => note.text)
        renderStreamingMessage(observation)
      }
      const previousCall = currentToolCalls.value.find((call) => call.providerCallId === payload.providerCallId)
      const firstSequence =
        typeof payload.firstSequence === 'number'
          ? payload.firstSequence
          : (previousCall?.firstSequence ?? event.sequence)
      upsertReceipt({ ...payload, firstSequence }, progress.step)
      const parsed = ReceiptSchema.partial().safeParse({ ...payload, firstSequence })
      if (parsed.success) {
        observation.receipts ??= []
        const receipts = observation.receipts
        const index = receipts.findIndex((receipt) => receipt.providerCallId === parsed.data.providerCallId)
        const complete = ReceiptSchema.safeParse({ ...receipts[index], ...parsed.data })
        if (complete.success) {
          if (index < 0) {
            receipts.push(complete.data)
          } else {
            receipts[index] = complete.data
          }
        }
      }
      progress.tools = currentToolCalls.value
      isResearching.value = currentToolCalls.value.some((call) => call.status === 'running')
      rendered = true
    } else if (event.type === 'approval') {
      const parsed = z.object({ receiptId: z.number(), approval: ApprovalSchema }).safeParse(payload)
      if (parsed.success) {
        const receipt = observation.receipts?.find((item) => item.id === parsed.data.receiptId)
        if (receipt) {
          receipt.approval = parsed.data.approval
        } else {
          void loadTurnSnapshot(observation, generation).catch(() => {})
        }
      }
    } else if (event.type === 'paused') {
      observation.state = 'awaiting_approval'
      isStreaming.value = false
      isResearching.value = false
    } else if (event.type === 'resumed') {
      observation.state = 'running'
      isStreaming.value = true
    }
    observation.cursor = event.sequence
    persistPendingObservation(observation)
    if (rendered && observation.turnId) {
      const turnId = observation.turnId
      void nextTick(() => {
        reportTurnTiming(teamPid.value, turnId, observation.clientTurnId, 'firstRenderMs')
        if (event.type === 'text_delta' || event.type === 'phase_message_delta') {
          reportTurnTiming(teamPid.value, turnId, observation.clientTurnId, 'firstTextMs')
        }
      })
    }
    if (event.type === 'terminal' && !settlingTurn) {
      settlingTurn = true
      stopObserving()
      void loadTurnSnapshot(observation, generation).catch(() => {
        if (isActiveObservation(observation, generation)) {
          isStreaming.value = false
        }
      })
    }
  }

  async function reconcileTurn(observation: PendingTurnObservation, generation: number) {
    if (!observation.turnId || !isActiveObservation(observation, generation)) {
      return
    }
    stopObserving()
    settlingTurn = false
    const progress: RightyTurnProgress = observation.progress ?? {
      started: false,
      step: 0,
      content: '',
      interim: [],
      tools: []
    }
    // Older observations stored a cursor without the text it covered.
    if (!observation.progress) {
      observation.cursor = 0
    }
    observation.progress = progress
    streamingContent.value = progress.content
    interimText.value = progress.interim.map((note) => note.text)
    hasTurnStarted.value = progress.started
    currentToolCalls.value = progress.tools
    await loadTurnSnapshot(observation, generation)
    if (!isActiveObservation(observation, generation)) {
      return
    }
    renderStreamingMessage(observation)
    streamObserver = observeTurnStream({
      teamPid: teamPid.value,
      threadId: observation.payload.threadId,
      turnId: observation.turnId,
      cursor: () => observation.cursor,
      apply: (event) => applyTurnEvent(event, observation, generation),
      onError: () => {
        if (isActiveObservation(observation, generation)) {
          setDurableError('stream', 'Turn accepted, but updates could not be observed.')
        }
      }
    })
    await streamObserver.replay()
  }

  async function stopTurn() {
    const observation = pendingObservation.value
    const generation = observationGeneration.value
    if (!observation?.turnId || stopRequested.value) {
      return
    }
    stopRequested.value = true
    try {
      await api.post(`/api/${teamPid.value}/righty/turns/${observation.turnId}/stop`)
    } catch {
      if (isActiveObservation(observation, generation)) {
        stopRequested.value = false
        setDurableError('general', 'Righty could not stop this request. Please try Stop again.')
      }
    }
  }

  async function decideApproval(receiptId: number, decision: 'approve' | 'cancel') {
    const observation = pendingObservation.value
    const generation = observationGeneration.value
    if (!observation?.turnId) {
      return
    }
    try {
      const { data } = await api.post<unknown>(
        `/api/${teamPid.value}/righty/turns/${observation.turnId}/receipts/${receiptId}/approval`,
        { decision }
      )
      const { turn } = z.object({ turn: TurnSnapshotSchema }).parse(data)
      if (isActiveObservation(observation, generation)) {
        applyAndSettleTurnSnapshot(turn, observation)
      }
    } catch (error) {
      if (!isActiveObservation(observation, generation)) {
        return
      }
      const conflict = z
        .object({ response: z.object({ data: z.object({ turn: TurnSnapshotSchema }) }) })
        .safeParse(error)
      if (conflict.success) {
        applyAndSettleTurnSnapshot(conflict.data.response.data.turn, observation)
      }
      setDurableError('general', 'Righty could not save your decision. Please try again.')
    }
  }

  // Recovered observations replay the plan after a reload, so recorded events live in session
  // storage for the tab rather than only in memory.
  const PLAYBOOK_ANALYTICS_KEY = 'righty:playbook-analytics'
  const readRecordedPlaybookEvents = (): Set<string> => {
    try {
      const stored: unknown = JSON.parse(window.sessionStorage.getItem(PLAYBOOK_ANALYTICS_KEY) ?? '[]')
      return new Set(Array.isArray(stored) ? stored.filter((entry): entry is string => typeof entry === 'string') : [])
    } catch {
      return new Set()
    }
  }
  const recordedPlaybookEvents = readRecordedPlaybookEvents()
  const markPlaybookEventRecorded = (key: string): boolean => {
    if (recordedPlaybookEvents.has(key)) {
      return false
    }
    recordedPlaybookEvents.add(key)
    try {
      window.sessionStorage.setItem(PLAYBOOK_ANALYTICS_KEY, JSON.stringify([...recordedPlaybookEvents].slice(-200)))
    } catch {
      // The in-memory set still prevents duplicates for this page load.
    }
    return true
  }

  function recordPlaybookProgress(plan: RightyPlanWire, turnId: string) {
    if (!plan.playbook) {
      return
    }
    const key = `${teamPid.value}:${turnId}`
    const doneCount = plan.todos.filter((todo) => todo.state === 'done').length
    for (const todo of plan.todos) {
      if (todo.state !== 'done' || !markPlaybookEventRecorded(`${key}:todo:${todo.id}`)) {
        continue
      }
      void recordAnalyticsEvent(AnalyticsEventTypes.ONBOARDING_LIVE_BUILD_STEP_COMPLETED, {
        playbook: plan.playbook,
        todoId: todo.id,
        doneCount,
        totalCount: plan.todos.length
      })
    }
  }

  function upsertPhaseMessage(phase: BuildPhaseMessage) {
    const existing = messages.value.find((message) => message.id === phase.id)
    if (existing && (existing.metadata?.buildPhase?.attempt ?? -1) > phase.metadata.buildPhase.attempt) {
      return
    }
    if (existing) {
      existing.content = phase.content
      existing.metadata = { ...existing.metadata, ...klona(phase.metadata) }
    } else {
      messages.value.push({ ...klona(phase), role: 'assistant', createdAt: new Date().toISOString() })
    }
  }

  function applyTurnSnapshot(turn: DurableTurnSnapshot) {
    const observation = pendingObservation.value
    if (observation?.turnId === turn.id && turn.lastEventSequence < observation.cursor) {
      return false
    }
    if (turn.plan) {
      recordPlaybookProgress(turn.plan, turn.id)
    }
    for (const message of turn.phaseMessages ?? []) {
      upsertPhaseMessage(message)
    }
    if (observation?.turnId === turn.id) {
      observation.state = turn.state
      observation.receipts = turn.receipts
      if (turn.plan !== undefined) {
        observation.plan = turn.plan
      }
      if (turn.plan?.stage && observation.buildStage?.stage !== turn.plan.stage) {
        const stage = turn.plan.stage
        observation.buildStage = { stage, unitId: null, label: buildStageLabels[stage], startedAt: Date.now() }
      }
      if (turn.phaseMessages) {
        observation.phaseMessages = turn.phaseMessages
      }
      if (turn.buildProgress !== undefined) {
        if (turn.buildProgress) {
          observation.buildProgress = turn.buildProgress
        } else {
          delete observation.buildProgress
        }
      }
      isStreaming.value = turn.state === 'submitted' || turn.state === 'running'
      if (observation.progress && turn.steps) {
        const progress = observation.progress
        progress.step = Math.max(
          0,
          ...turn.steps.map((step) => step.step),
          ...turn.receipts.map((receipt) => receipt.step ?? 0)
        )
        progress.content = turn.steps.find((step) => step.step === progress.step)?.text ?? ''
        progress.interim = turn.steps.filter((step) => step.step < progress.step)
        streamingContent.value = progress.content
        interimText.value = progress.interim.map((note) => note.text)
      }
      if (turn.steps || turn.phaseMessages) {
        observation.cursor = turn.lastEventSequence
      }
      persistPendingObservation(observation)
    }
    if (turn.taskState === 'active') {
      activeTaskId.value = turn.taskId
    }
    if (turn.state === 'waiting') {
      waitingTurnId.value = turn.id
      pendingQuestion.value = questionFromMetadata(turn.assistant?.metadata, turn.assistant?.content)
    } else if (waitingTurnId.value === turn.id) {
      waitingTurnId.value = null
      pendingQuestion.value = null
    }
    if (
      ['completed', 'handed_off', 'superseded'].includes(turn.taskState ?? '') &&
      turn.taskId === activeTaskId.value
    ) {
      activeTaskId.value = null
    }
    for (const receipt of turn.receipts || []) {
      upsertReceipt(receipt)
    }
    if (['succeeded', 'failed', 'waiting', 'stopped'].includes(turn.state)) {
      // A call that started streaming but never got a receipt (the step broke off) did not run.
      for (const call of currentToolCalls.value) {
        if (call.status === 'running') {
          call.status = turn.state === 'stopped' ? 'stopped' : 'error'
        }
      }
    }
    if (turn.assistant) {
      const backendChangelog = turn.assistant.metadata?.planApplied
      const serverPlan = PlanSchema.safeParse(turn.assistant.metadata?.plan)
      const plan = serverPlan.success ? serverPlan.data : (turn.plan ?? observation?.plan ?? undefined)
      if (plan) {
        recordPlaybookProgress(plan, turn.id)
      }
      const configMutated =
        turn.assistant.metadata?.configMutated === true ||
        (backendChangelog &&
          typeof backendChangelog === 'object' &&
          'changelog' in backendChangelog &&
          Array.isArray(backendChangelog.changelog) &&
          backendChangelog.changelog.length > 0) ||
        turn.receipts.some((receipt) => receipt.kind === 'mutation' && receipt.state === 'succeeded')
      const message: RightyMessage = {
        id: turn.assistant.id,
        role: 'assistant',
        content:
          turn.state === 'stopped' && (!turn.assistant.content || turn.assistant.content === 'Stopped.')
            ? streamingContent.value || 'Stopped.'
            : turn.assistant.content || '',
        createdAt: new Date().toISOString(),
        metadata: {
          ...turn.assistant.metadata,
          ...(plan ? { plan } : {}),
          rightyTurnId: turn.id,
          rightyProgress: pendingObservation.value?.progress,
          rightyStopped: turn.state === 'stopped',
          receipts: turn.receipts,
          failure: turn.failure,
          ...(configMutated ? { configMutated: true } : {})
        }
      }
      const existingIndex = messages.value.findIndex(
        (existing) =>
          existing.role === 'assistant' &&
          !existing.metadata?.buildPhase &&
          (existing.id === message.id || existing.metadata?.rightyTurnId === turn.id)
      )
      if (existingIndex === -1) {
        messages.value.push(message)
      } else {
        messages.value[existingIndex] = message
        for (let index = messages.value.length - 1; index > existingIndex; index--) {
          const duplicate = messages.value[index]
          if (
            duplicate?.role === 'assistant' &&
            !duplicate.metadata?.buildPhase &&
            (duplicate.id === message.id || duplicate.metadata?.rightyTurnId === turn.id)
          ) {
            messages.value.splice(index, 1)
          }
        }
      }
    }
    if (turn.state === 'failed') {
      setDurableError('terminal', FAILED_TURN_MESSAGE)
    }
    return true
  }

  function upsertReceipt(
    receipt: Record<string, unknown> | DurableTurnSnapshot['receipts'][number],
    currentStep?: number
  ) {
    const providerCallId = typeof receipt.providerCallId === 'string' ? receipt.providerCallId : null
    const tool = typeof receipt.tool === 'string' ? receipt.tool : 'Righty tool'
    if (!providerCallId) {
      return
    }
    const state = ['succeeded', 'waiting_for_user', 'handed_off'].includes(String(receipt.state))
      ? 'completed'
      : receipt.state === 'failed'
        ? 'error'
        : receipt.state === 'cancelled'
          ? 'cancelled'
          : 'running'
    const description = typeof receipt.label === 'string' ? receipt.label : undefined
    const step = typeof receipt.step === 'number' ? receipt.step : undefined
    const error =
      receipt.error &&
      typeof receipt.error === 'object' &&
      'message' in receipt.error &&
      typeof receipt.error.message === 'string'
        ? receipt.error.message
        : undefined
    const existing = currentToolCalls.value.find(
      (call) => (call.providerCallId ?? call.arguments?.providerCallId) === providerCallId
    )
    if (existing) {
      existing.providerCallId = providerCallId
      if (typeof receipt.firstSequence === 'number') {
        existing.firstSequence = receipt.firstSequence
      }
      if (step !== undefined) {
        existing.step = step
      }
      existing.name = tool
      existing.status = state
      existing.error = error
      if (description) {
        existing.description = description
      }
    } else {
      currentToolCalls.value.push({
        name: tool,
        step: step ?? currentStep,
        providerCallId,
        firstSequence: typeof receipt.firstSequence === 'number' ? receipt.firstSequence : undefined,
        status: state,
        description,
        arguments: { providerCallId },
        error
      })
    }
  }

  function questionFromMetadata(
    metadata: Record<string, unknown> | undefined,
    fallback: string | null | undefined
  ): StreamSegment {
    const question = metadata?.question
    if (question && typeof question === 'object' && !Array.isArray(question)) {
      const record = question as Record<string, unknown>
      const options = Array.isArray(record.options)
        ? record.options
            .filter(
              (option): option is { label: string; description?: string | null } =>
                typeof option === 'object' &&
                option !== null &&
                typeof (option as Record<string, unknown>).label === 'string'
            )
            .map((option) => ({ label: option.label, description: option.description }))
        : []
      const allowCustom =
        typeof record.allow_custom === 'boolean'
          ? record.allow_custom
          : typeof record.allowCustom === 'boolean'
            ? record.allowCustom
            : true
      return {
        type: 'question',
        question: typeof record.question === 'string' ? record.question : fallback || 'What would you like to do?',
        options,
        allowCustom
      }
    }
    return { type: 'question', question: fallback || 'What would you like to do?', options: [], allowCustom: true }
  }

  class DefinitiveSubmitError extends Error {
    override name = 'DefinitiveSubmitError'
  }

  function isDefinitiveSubmitFailure(error: unknown): boolean {
    if (!error || typeof error !== 'object' || !('response' in error)) {
      return false
    }
    const response = error.response
    if (!response || typeof response !== 'object' || !('status' in response) || typeof response.status !== 'number') {
      return false
    }
    return response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429
  }

  function submitFailureMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'response' in error) {
      const response = error.response
      if (response && typeof response === 'object' && 'data' in response) {
        const data = response.data
        if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
          return data.message
        }
      }
    }
    return errorMessage(error, 'Righty rejected this request.')
  }

  /** The reason a turn could not be submitted or observed, worded for the drawer and for sendMessage callers. */
  function continueFailureMessage(error: unknown): string {
    if (isAxiosError<{ message?: unknown }>(error)) {
      const status = error.response?.status
      const serverMessage = error.response?.data?.message
      if (status === undefined) {
        return 'Righty could not reach the server. Check your connection and try again.'
      }
      const reason = typeof serverMessage === 'string' && serverMessage.trim() ? serverMessage.trim() : error.message
      return `Righty could not continue this request: ${reason} (HTTP ${status}).`
    }
    return errorMessage(error, 'Righty could not continue this request.')
  }

  function isActiveObservation(observation: PendingTurnObservation, generation: number): boolean {
    return (
      observationGeneration.value === generation &&
      currentThread.value?.id === observation.payload.threadId &&
      pendingObservation.value?.clientTurnId === observation.clientTurnId
    )
  }

  function restoreOptimisticUserMessage(observation: PendingTurnObservation) {
    if (messages.value.some((message) => message.id === observation.optimisticMessageId)) {
      return
    }
    messages.value.push({
      id: observation.optimisticMessageId,
      role: 'user',
      content: observation.payload.text,
      pageContext: {
        path: observation.payload.pageContext.currentPage,
        title: observation.payload.pageContext.pageTitle
      },
      ...(observation.attachments?.length ? { metadata: { attachments: observation.attachments } } : {}),
      createdAt: new Date().toISOString()
    })
  }

  function reconcileOptimisticUserMessage(observation: PendingTurnObservation, userMessageId: number) {
    const optimisticIndex = messages.value.findIndex((message) => message.id === observation.optimisticMessageId)
    const persisted = messages.value.some((message) => message.id === userMessageId)
    if (optimisticIndex !== -1 && persisted) {
      messages.value.splice(optimisticIndex, 1)
    } else if (optimisticIndex !== -1) {
      const optimisticMessage = messages.value[optimisticIndex]
      if (optimisticMessage === undefined) {
        throw new Error('Optimistic Righty message is missing')
      }
      optimisticMessage.id = userMessageId
    }
    observation.optimisticMessageId = userMessageId
  }

  async function retryObservation() {
    const observation = pendingObservation.value
    const generation = observationGeneration.value
    if (!observation || !isActiveObservation(observation, generation)) {
      return
    }
    clearError()
    isStreaming.value = true
    streamingStartTime.value = Date.now()
    try {
      if (!observation.turnId) {
        const turn = await submitTurn(observation.payload)
        if (!isActiveObservation(observation, generation)) {
          return
        }
        observation.turnId = turn.turnId
        activeTaskId.value = turn.taskId
        reconcileOptimisticUserMessage(observation, turn.userMessageId)
        if (waitingTurnId.value === observation.payload.parentTurnId) {
          waitingTurnId.value = null
          pendingQuestion.value = null
        }
        persistPendingObservation(observation)
      }
      await reconcileTurn(observation, generation)
    } catch (err) {
      if (!isActiveObservation(observation, generation)) {
        return
      }
      if (err instanceof DefinitiveSubmitError) {
        clearPendingObservation(observation)
        setDurableError('general', err.message)
      } else if (!durableError.value) {
        setDurableError(observation.turnId ? 'snapshot' : 'submit', continueFailureMessage(err))
      }
      isStreaming.value = false
      streamingStartTime.value = null
      isResearching.value = false
    }
  }
  function observationStorageKey(threadId: number): string {
    return `righty:pending-turn:${teamPid.value}:${threadId}`
  }

  function persistPendingObservation(observation: PendingTurnObservation) {
    pendingObservation.value = observation
    sessionStorage.setItem(observationStorageKey(observation.payload.threadId), JSON.stringify(observation))
  }

  function loadPendingObservation(threadId: number): PendingTurnObservation | null {
    const raw = sessionStorage.getItem(observationStorageKey(threadId))
    if (!raw) {
      return null
    }
    try {
      const parsed = PendingTurnObservationSchema.safeParse(JSON.parse(raw))
      if (!parsed.success || parsed.data.payload.threadId !== threadId) {
        return null
      }
      return parsed.data
    } catch {
      return null
    }
  }

  function clearPendingObservation(observation = pendingObservation.value) {
    if (!observation) {
      return
    }
    sessionStorage.removeItem(observationStorageKey(observation.payload.threadId))
    if (pendingObservation.value?.clientTurnId === observation.clientTurnId) {
      pendingObservation.value = null
    }
  }

  function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback
  }

  function setDurableError(kind: DurableTurnErrorKind, message: string) {
    durableError.value = { kind, message }
    error.value = message
  }

  async function retryFailedRequest() {
    const failedPayload = lastFailedPayload.value
    if (!failedPayload || pendingObservation.value) {
      return
    }
    clearError()
    await sendMessage(failedPayload.text, {
      plan: failedPayload.plan,
      label: failedPayload.label,
      attachments: lastFailedAttachments.value
    })
  }

  function sleep(ms: number): Promise<void> {
    const { promise, resolve } = (Promise as WithResolversPromiseConstructor).withResolvers<void>()
    setTimeout(resolve, ms)
    return promise
  }

  /**
   * Answer a pending question (from ask_user tool) without ever reopening its
   * terminal parent. The parent identifier remains attached until the child turn
   * has been accepted by the durable endpoint.
   */
  async function answerQuestion(answer: string) {
    const lastAssistantMsg = [...messages.value].reverse().find((m) => m.role === 'assistant')
    if (lastAssistantMsg?.metadata?.segments) {
      const segments = lastAssistantMsg.metadata.segments as StreamSegment[]
      const questionSeg = segments.find((s) => s.type === 'question')
      if (questionSeg) {
        questionSeg.selectedOption = answer
      }
    }
    if (pendingQuestion.value) {
      pendingQuestion.value.selectedOption = answer
    }
    streamingSegments.value = []
    await sendMessage(answer)
  }

  function clearPendingAction() {
    pendingAction.value = null
  }

  function clearError() {
    error.value = null
    durableError.value = null
  }

  // A turn keeps running on the server when the customer switches conversations, so it is
  // watched here until it settles; `isTurnRunning` covers it and the visible turn.
  const backgroundTurnIds = ref<string[]>([])
  const isTurnRunning = computed(() => isStreaming.value || backgroundTurnIds.value.length > 0)
  /** The page a chained layer setup turn is for, while the dock attaches to it and has no observation yet. */
  const chainingPageId = ref<string | null>(null)
  /**
   * The hosted page an observed turn works on: the page editor it was sent from, or the page of the chained layer setup
   * turn its request is (that request carries no editor context of its own on this side).
   */
  function observationPageId(observation: PendingTurnObservation): string | null {
    const fromEditor = observation.payload.pageContext.context?.pageEditor?.pageId
    if (fromEditor) {
      return fromEditor
    }
    const chained = messages.value.find((message) => message.id === observation.optimisticMessageId)?.metadata?.chained
    return chained?.kind === 'layers' ? chained.pageId : null
  }
  /** The hosted page the observed turn works on, so that page's editor can hold Undo and its layers while it works. */
  const turnPageId = computed(() => {
    if (!isStreaming.value) {
      return null
    }
    const observation = pendingObservation.value
    return observation ? observationPageId(observation) : chainingPageId.value
  })
  const settledPageTurn = shallowRef<SettledPageTurn | null>(null)

  function watchBackgroundTurn(turnId: string, pid: string) {
    if (backgroundTurnIds.value.includes(turnId)) {
      return
    }
    backgroundTurnIds.value = [...backgroundTurnIds.value, turnId]
    const settle = () => {
      backgroundTurnIds.value = backgroundTurnIds.value.filter((id) => id !== turnId)
    }
    let failures = 0
    const poll = (): void => {
      api
        .get<{ turn?: { state?: string } }>(`/api/${pid}/righty/turns/${turnId}`)
        .then(({ data }) => {
          failures = 0
          if (data.turn?.state === 'submitted' || data.turn?.state === 'running') {
            setTimeout(poll, 3000)
            return
          }
          settle()
        })
        .catch((error: unknown) => {
          // Only the server ends the watch: a dropped check must not unlock the canvas while Righty
          // still edits the draft. A missing or forbidden turn will never report, so it settles.
          const status = isAxiosError(error) ? error.response?.status : undefined
          if (status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429) {
            settle()
            return
          }
          failures += 1
          setTimeout(poll, Math.min(3000 * 2 ** failures, 30_000))
        })
    }
    setTimeout(poll, 3000)
  }

  function resetConversationState(readyToChat: boolean) {
    const running = pendingObservation.value
    if (isStreaming.value && running?.turnId && teamPid.value) {
      watchBackgroundTurn(running.turnId, String(teamPid.value))
    }
    observationGeneration.value++
    stopObserving()
    hasTurnStarted.value = false
    interimText.value = []
    stopRequested.value = false
    pendingObservation.value = null
    currentThread.value = null
    matrixSelectionContext = null
    matrixSelectionRequest = undefined
    messages.value = []
    streamingContent.value = ''
    streamingSegments.value = []
    currentToolCalls.value = []
    pendingAction.value = null
    pendingQuestion.value = null
    lastFailedPayload.value = null
    lastFailedAttachments.value = []
    clearError()
    isStreaming.value = false
    streamingStartTime.value = null
    isResearching.value = false
    activeTaskId.value = null
    waitingTurnId.value = null
    isReadyToChat.value = readyToChat
  }

  function clearCurrentThread() {
    resetConversationState(false)
  }

  function setInvocationContext(newContext: RightyInvocationContext) {
    invocationContext.value = newContext
  }

  /**
   * Sets nested context for drawer content.
   * This overlays additional context on top of the base route context.
   * Called by drawer-hosting components when they open nested content.
   */
  function setNestedContext(nestedContext: RightyNestedContext) {
    if (invocationContext.value) {
      invocationContext.value = {
        ...invocationContext.value,
        nestedContext
      }
    }
  }

  /**
   * Clears nested context when drawer closes.
   * Called by drawer-hosting components on drawer hide.
   */
  function clearNestedContext() {
    if (invocationContext.value?.nestedContext) {
      const { nestedContext, ...baseContext } = invocationContext.value
      invocationContext.value = baseContext as RightyInvocationContext
    }
  }

  async function startNewConversation() {
    restoredThreadId = null
    resetConversationState(true)
  }

  async function startMatrixSelectionChat(input: Omit<MatrixSelectionRequest, 'opening'>) {
    if (capturingEditorContext) {
      return
    }
    openDrawer()
    await startNewConversation()
    matrixSelectionRequest = {
      cells: input.cells.map((cell) => ({ ...cell })),
      pageUrl: input.pageUrl,
      opening: true
    }
    await sendMessage(
      input.cells.length === 1 ? 'I want to rewrite this cell.' : `I want to rewrite these ${input.cells.length} cells.`
    )
  }

  async function deleteCurrentThread() {
    if (currentThread.value) {
      await deleteThread(currentThread.value.id)
      return
    }
    resetConversationState(true)
  }
  /**
   * Save a snapshot of the current config before executing a plan.
   * This allows the user to undo/restore if they don't like the result.
   */
  function saveConfigSnapshot() {
    const configStore = useConfigStore()
    if (configStore.config) {
      configSnapshotBeforeBuild.value = klona(toRaw(configStore.config))
    }
  }

  /**
   * Restore the config to its state before the last plan execution.
   * Returns true if restoration was successful, false if no snapshot exists.
   */
  function restoreConfigSnapshot(): boolean {
    if (!configSnapshotBeforeBuild.value) {
      return false
    }

    const configStore = useConfigStore()
    configStore.config = klona(configSnapshotBeforeBuild.value)

    // Clear snapshot and changelog after restore
    configSnapshotBeforeBuild.value = null
    lastBuildChangelog.value = []

    return true
  }

  /**
   * Clear the snapshot without restoring (e.g., when user confirms the changes are good)
   */
  function clearSnapshot() {
    configSnapshotBeforeBuild.value = null
    lastBuildChangelog.value = []
  }

  /** Undo only the persisted changes belonging to the selected assistant message. */
  async function undoLastBuild(
    messageId: number
  ): Promise<{ success: true; resourceType: string } | { success: false; error: string }> {
    if (!teamPid.value || teamPid.value === 'demo' || !currentThread.value) {
      return { success: false, error: 'Open the conversation containing these changes before undoing them.' }
    }

    undoingMessageId.value = messageId
    try {
      const { data } = await api.post(`/api/${teamPid.value}/righty/undo`, {
        threadId: currentThread.value.id,
        messageId,
        tabId: resolveEditorTabId()
      })
      const result = z.object({ success: z.literal(true), resourceType: z.enum(['split_test', 'config']) }).parse(data)
      lastBuildChangelog.value = []
      const message = messages.value.find((item) => item.id === messageId)
      if (message) {
        message.metadata = { ...message.metadata, undoneAt: new Date().toISOString() }
      }
      builtMessageChangelogs.value.delete(messageId)
      return result
    } catch (err: unknown) {
      const failure = z
        .object({
          response: z.object({ data: z.object({ error: z.string().optional(), message: z.string().optional() }) })
        })
        .safeParse(err)
      return {
        success: false,
        error:
          (failure.success && (failure.data.response.data.error || failure.data.response.data.message)) ||
          'Unable to undo these changes. Please try again.'
      }
    } finally {
      undoingMessageId.value = null
    }
  }

  /**
   * Set the changelog from a build execution result
   */
  function setChangelog(changelog: ChangelogItem[]) {
    lastBuildChangelog.value = changelog
  }

  /**
   * Set the changelog for a specific message (after plan build completes).
   * This persists the changelog with the message so it stays visible in the thread.
   */
  function setMessageChangelog(messageId: number, changelog: ChangelogItem[]) {
    builtMessageChangelogs.value.set(messageId, changelog)
    // Also update lastBuildChangelog for backwards compatibility (getRecentCreationsForContext)
    lastBuildChangelog.value = changelog
  }

  /**
   * Get the changelog for a specific message, if it has been built.
   */
  function getMessageChangelog(messageId: number): ChangelogItem[] {
    return builtMessageChangelogs.value.get(messageId) || []
  }

  /**
   * Check if a message's plan has been built.
   */
  function isMessageBuilt(messageId: number): boolean {
    return builtMessageChangelogs.value.has(messageId)
  }

  /**
   * Get recent creations from the last build for passing to the AI.
   * This enables Righty to know what was just created so it can modify instead of duplicate.
   * Returns the last 5 relevant items (yesno, question, offer, dimension).
   */
  function getRecentCreationsForContext(): RecentCreation[] {
    return lastBuildChangelog.value
      .filter((item) => ['yesno', 'question', 'offer', 'dimension'].includes(item.resourceType))
      .slice(-5)
      .map((item) => ({
        resourceType: item.resourceType,
        name: item.name,
        id: item.id
      }))
  }

  /**
   * Report completed build actions to the backend for persistent storage.
   * This allows Claude to know what was already built across page refreshes
   * and long conversations, preventing it from recreating the same resources.
   */
  async function reportBuildCompletion(changelog: ChangelogItem[]) {
    // Skip if no actions to report
    if (!changelog || changelog.length === 0) {
      return
    }

    if (!teamPid.value || teamPid.value === 'demo' || !currentThread.value) {
      return
    }

    try {
      await api.post(`/api/${teamPid.value}/righty/threads/${currentThread.value.id}/actions`, { changelog })
    } catch (err) {
      // Non-critical error - don't show to user, just log
      console.error('Failed to report build completion:', err)
    }
  }

  /**
   * Register dynamic prompts from a component.
   * Used for conditional prompts that depend on component state (e.g., visitor identification).
   * These prompts are merged with route-based prompts for the palette and chat.
   */
  function registerDynamicPrompts(prompts: RightyPrompt[]) {
    dynamicPrompts.value = prompts
  }

  /**
   * Clear dynamic prompts.
   * Should be called when component unmounts or conditions change.
   */
  function clearDynamicPrompts() {
    dynamicPrompts.value = []
  }

  /**
   * Activate the empty assistant when empty content is detected.
   * Shows a synthetic welcome message and opens the drawer.
   * Generic function for any content type; blank emails use it today.
   *
   * @param type - The type of content (e.g., 'email')
   * @param id - The ID of the resource
   * @param message - The welcome message to display
   * @param name - Optional name of the resource
   */
  function activateEmptyAssistant(type: string, id: string, message: string, name?: string) {
    isEmptyAssistantActive.value = true
    emptyAssistantContext.value = { type, id, name }
    syntheticWelcomeMessage.value = {
      id: -1,
      role: 'assistant',
      content: message,
      createdAt: new Date().toISOString()
    }
    isReadyToChat.value = true
    openDrawer()
  }

  /**
   * Deactivate the empty assistant.
   * Called when content is no longer empty or user navigates away.
   */
  function deactivateEmptyAssistant() {
    isEmptyAssistantActive.value = false
    syntheticWelcomeMessage.value = null
    emptyAssistantContext.value = null
  }

  watch(
    [isDrawerOpen, () => currentThread.value?.id ?? restoredThreadId],
    ([open, threadId]) => {
      try {
        sessionStorage.setItem('righty:sidebar', JSON.stringify({ open, threadId: threadId ?? null }))
      } catch {
        // The sidebar remains usable when storage is unavailable.
      }
    },
    { flush: 'sync' }
  )
  if (sidebarState.open) {
    void nextTick(() => loadThreads())
  }

  return {
    // State
    isDrawerOpen,
    focusInputOnOpen,
    undoingMessageId,
    threads,
    currentThread,
    messages,
    isLoading,
    isStreaming,
    isTurnRunning,
    turnPageId,
    settledPageTurn,
    hasTurnStarted,
    interimText,
    stopRequested,
    streamingContent,
    streamingSegments,
    pendingQuestion,
    streamingStartTime,
    isResearching,
    pendingAction,
    error,
    currentToolCalls,
    isReadyToChat,
    invocationContext,
    dynamicPrompts: readonly(dynamicPrompts),
    configSnapshotBeforeBuild,
    lastBuildChangelog,
    builtMessageChangelogs,
    syntheticWelcomeMessage,
    isEmptyAssistantActive,
    emptyAssistantContext,
    pendingObservation,
    durableError,
    activeTaskId,
    waitingTurnId,
    // Type-specific computed aliases
    isEmptyEmailAssistantActive,

    // Computed
    inputMode,
    hasThreads,
    currentPage,
    canUndo,
    contextualSuggestions,
    externalChipPrompts,

    // Actions
    openDrawer,
    closeDrawer,
    toggleDrawer,
    loadThreads,
    createThread,
    selectThread,
    deleteThread,
    sendMessage,
    registerCampaignEditorContext,
    registerPageEditorContext,
    stopTurn,
    decideApproval,
    retryObservation,
    retryFailedRequest,
    answerQuestion,
    clearPendingAction,
    clearError,
    clearCurrentThread,
    setInvocationContext,
    setNestedContext,
    clearNestedContext,
    startNewConversation,
    startMatrixSelectionChat,
    deleteCurrentThread,
    saveConfigSnapshot,
    restoreConfigSnapshot,
    undoLastBuild,
    clearSnapshot,
    setChangelog,
    setMessageChangelog,
    getMessageChangelog,
    isMessageBuilt,
    getRecentCreationsForContext,
    reportBuildCompletion,
    registerDynamicPrompts,
    clearDynamicPrompts,
    activateEmptyAssistant,
    deactivateEmptyAssistant
  }
})
